import React, { useState } from "react";
import HeaderMobile from "./HeaderMobile.js";
import Header from "./Header.js";
import Sidebar from "./Sidebar.js";
import Footer from "./Footer.js";
import { BASE_URL } from "./Constants.js";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const OpeningStockReport = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [stockData, setStockData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [areaFilter, setAreaFilter] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const handleToggleSidebar = () => {
        if (window.innerWidth >= 992) {
            setIsSidebarOpen((prev) => !prev);
        }
    };

    const fetchStockReport = async () => {
        if (!fromDate || !toDate) {
            alert("Please select From and To dates");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${BASE_URL}opening_stock_report`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    from_date: fromDate,
                    to_date: toDate,
                }),
            });

            const data = await response.json();

            if (data.status && Array.isArray(data.data)) {
                setStockData(data.data);
            } else {
                setStockData([]);
                setError("No data available");
            }

        } catch (err) {
            setError("Failed to fetch data");
        }

        setLoading(false);
    };

    // Filter only by search and area
    const filteredStock = stockData.filter((item) => {
        const searchMatch = Object.values(item)
            .join(" ")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());

        const areaMatch = !areaFilter || item.area_id == areaFilter;

        return searchMatch && areaMatch;
    });

    // Pagination Logic
    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentStock = filteredStock.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredStock.length / itemsPerPage);

    // Dropdown Area List
    const areaList = [...new Map(stockData.map((it) => [it.area_id, it])).values()];

    const handleExcelExport = () => {
        const exportData = filteredStock.map((item) => ({
            Area: item.area_name,
            "Dress Type": item.dress_type_name,
            Size: item.dress_size_name,
            Color: item.dress_color_name,
            Gender: item.gender,
            "Opening Stock": item.opening_stock,
            "Received Stock": item.received_stock,
            "Total Stock": item.total_stock,
            "Issued Stock": item.issued_stock,
            "Closing Stock": item.closing_stock,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Opening Stock");

        saveAs(
            new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })]),
            "Opening_Stock_Report.xlsx"
        );
    };

    return (
        <div className="page-wrapper">
            <HeaderMobile />
            <Sidebar isOpen={isSidebarOpen} />

            <div className="page-container" style={{ marginLeft: isSidebarOpen ? "250px" : "0" }}>
                <Header onToggleSidebar={handleToggleSidebar} isSidebarOpen={isSidebarOpen} />

                <div className="main-content">
                    <div className="section__content section__content--p30">
                        <div className="container-fluid">

                            <h3 className="mb-3">Opening Stock Report</h3>
                            <h5 className="mb-3">Please select from date and to date to get data</h5>

                            {/* Filters Section */}
                            <div className="d-flex flex-wrap align-items-end mb-3">

                                {/* From Date */}
                                <div className="me-3 mb-2" style={{ display: "flex", flexDirection: "column" }}>
                                    <label className="form-label" style={{ fontSize: "13px", fontWeight: "bold" }}>
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        style={{ width: "180px" }}
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </div>

                                {/* To Date */}
                                <div className="me-3 mb-2" style={{ display: "flex", flexDirection: "column" }}>
                                    <label className="form-label" style={{ fontSize: "13px", fontWeight: "bold" }}>
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        style={{ width: "180px" }}
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </div>

                                {/* Search Button */}
                                <div className="me-3 mb-2 d-flex align-items-end">
                                    <button
                                        className="btn btn-primary"
                                        style={{ height: "38px", padding: "0 20px" }}
                                        onClick={fetchStockReport}
                                    >
                                        Search
                                    </button>
                                </div>

                                {stockData.length > 0 && (
                                    <>
                                        {/* Search Filter */}
                                        <div className="me-3 mb-2" style={{ display: "flex", flexDirection: "column" }}>
                                            <label className="form-label" style={{ fontSize: "13px", fontWeight: "bold" }}>
                                                Search
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search..."
                                                style={{ width: "180px" }}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </div>

                                        {/* Area Filter */}
                                        <div className="me-3 mb-2" style={{ display: "flex", flexDirection: "column" }}>
                                            <label className="form-label" style={{ fontSize: "13px", fontWeight: "bold" }}>
                                                Area
                                            </label>
                                            <select
                                                className="form-control"
                                                style={{ width: "180px" }}
                                                value={areaFilter}
                                                onChange={(e) => setAreaFilter(e.target.value)}
                                            >
                                                <option value="">All Areas</option>
                                                {areaList.map((i) => (
                                                    <option key={i.area_id} value={i.area_id}>{i.area_name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Excel Export Button */}
                                        <div className="me-3 mb-2 d-flex align-items-end">
                                            <button
                                                className="btn btn-success"
                                                style={{ height: "38px", padding: "0 20px" }}
                                                onClick={handleExcelExport}
                                            >
                                                Excel
                                            </button>
                                        </div>
                                    </>
                                )}

                            </div>





                            {/* Table Section */}
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center">Loading...</div>
                                ) : error ? (
                                    <div className="text-danger text-center">{error}</div>
                                ) : (
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Area</th>
                                                <th>Dress Type</th>
                                                <th>Size</th>
                                                <th>Color</th>
                                                <th>Gender</th>
                                                <th>Opening</th>
                                                <th>Received</th>
                                                <th>Total</th>
                                                <th>Issued</th>
                                                <th>Closing</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentStock.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{indexOfFirst + index + 1}</td>
                                                    <td>{item.area_name}</td>
                                                    <td>{item.dress_type_name}</td>
                                                    <td>{item.dress_size_name}</td>
                                                    <td>{item.dress_color_name}</td>
                                                    <td>{item.gender}</td>
                                                    <td>{item.opening_stock}</td>
                                                    <td>{item.received_stock}</td>
                                                    <td>{item.total_stock}</td>
                                                    <td>{item.issued_stock}</td>
                                                    <td>{item.closing_stock}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Pagination */}
                            {filteredStock.length > 0 && (
                                <div className="d-flex justify-content-center mt-3">
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <button
                                            key={i}
                                            className={`btn btn-sm mx-1 ${currentPage === i + 1 ? "btn-primary" : "btn-light"}`}
                                            onClick={() => setCurrentPage(i + 1)}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="text-center mt-2">
                                Page {currentPage} of {totalPages}
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default OpeningStockReport;
